import { useRef, useState } from 'react';
import styles from './upload-zone.module.css';
import { TableActionsBar } from 'shared-components';
import { Button } from 'shared-components';
import { ButtonIntent } from 'shared-components';
import { ArrowUpTrayIcon } from '@heroicons/react/24/outline';

export interface IUploadZoneProps extends React.HTMLAttributes<HTMLDivElement> {
  onFileChange?: (files: File[]) => void;
  allowMultiple?: boolean;
  zoneMessage?: string;
  onlyOneMessage?: string;
}

export const UploadZone = ({
  children,
  onFileChange,
  zoneMessage,
  onlyOneMessage,
  allowMultiple = true,
  ...props
}: IUploadZoneProps) => {
  const [uploadList, setUploadList] = useState<File[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFilesAdded = (files: File[]) => {
    if (!allowMultiple && files.length > 1) {
      alert(onlyOneMessage ?? 'You can only upload one file at a time');
      return;
    }
    let newList = [];
    if (allowMultiple) {
      newList = [...uploadList, ...files];
    } else {
      newList = files.length > 0 ? [files[0]] : [];
    }
    setUploadList(newList);
    onFileChange?.(newList);
  };

  return (
    <div {...props}>
      {(uploadList.length === 0 || allowMultiple) && (
        <div
          onDragEnter={(e) => {
            e.preventDefault();
            // eslint-disable-next-line no-empty
            if (e.currentTarget === e.target) {
            }
          }}
          onDragOver={(e) => {
            e.preventDefault();
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            // eslint-disable-next-line no-empty
            if (e.currentTarget === e.target) {
            }
          }}
          onDrop={(ev) => {
            ev.preventDefault();
            if (ev.currentTarget === ev.target) {
              const files = [];
              if (ev.dataTransfer.items) {
                // Use DataTransferItemList interface to access the file(s)
                for (let i = 0; i < ev.dataTransfer.items.length; i++) {
                  const item = ev.dataTransfer.items[i];
                  // If dropped items aren't files, reject them
                  if (item.kind === 'file') {
                    const file = item.getAsFile();
                    if (file) {
                      files.push(file);
                    }
                  }
                }
              } else {
                // Use DataTransfer interface to access the file(s)
                for (let i = 0; i < ev.dataTransfer.files.length; i++) {
                  const file = ev.dataTransfer.files[i];
                  files.push(file);
                }
              }

              handleFilesAdded(files);
            }
          }}
          className={styles.zone}
          onClick={() => {
            inputRef.current?.click();
          }}
        >
          {children ? (
            children
          ) : (
            <>
              <div className={styles.icon}>
                <ArrowUpTrayIcon height={20} width={20} />
              </div>
              <div>
                {zoneMessage ??
                  (allowMultiple
                    ? 'Drop or click to add files'
                    : 'Drop or click to choose a file')}
              </div>
            </>
          )}
          <input
            ref={inputRef}
            type="file"
            multiple={allowMultiple}
            onChange={(event) => {
              const files = event.target.files;
              if (files) {
                handleFilesAdded(Array.from(files));
              }
            }}
            className={styles.input}
          />
        </div>
      )}

      {uploadList.length > 0 && (
        <div className={styles.fileList}>
          {allowMultiple && (
            <TableActionsBar
              singularActions={[
                {
                  value: 'clear',
                  display: 'Clear all',
                  action: () => {
                    setUploadList([]);
                  },
                },
              ]}
            />
          )}
          <table>
            <thead>
              <tr>
                <th>Preview</th>
                <th colSpan={2}>File</th>
              </tr>
            </thead>
            <tbody>
              {uploadList.map((upload) => (
                <tr key={upload.name}>
                  <td>
                    <img
                      className={styles.preview}
                      alt={upload.name}
                      src={URL.createObjectURL(upload)}
                    />
                  </td>
                  <td>{upload.name}</td>
                  <td className="text-right">
                    <Button
                      onClick={() => {
                        setUploadList(uploadList.filter((u) => u !== upload));
                      }}
                      intent={ButtonIntent.Negative}
                    >
                      Remove
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
